import { useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axiosLLInstance from "../../utils/axiosLLInstance";
import BreadcrumbItems from "../../components/BreadcrumbItems";
import { notifyUser } from "../../utils/notifyUser";
import { PermissionActions, Permissions } from "../../utils/constants";

export default function AssignRights() {
  const { userId } = useParams();
  const { users } = useSelector((state) => state.users);
  const user = users?.find((u) => String(u.id) === String(userId));

  const initialPermissions = JSON.parse(
    JSON.stringify(user?.permissions || Permissions)
  );

  const [permissions, setPermissions] = useState(initialPermissions);

  const togglePermission = (section, actionName) => {
    setPermissions((prev) => {
      const updated = structuredClone(prev);

      const sectionData = updated[section];

      sectionData.actions = sectionData.actions.map((a) =>
        a.name === actionName ? { ...a, value: !a.value } : a
      );

      if (actionName === PermissionActions.VIEW) {
        const viewAction = sectionData.actions.find(
          (a) => a.name === PermissionActions.VIEW
        );
        if (!viewAction.value) {
          sectionData.actions = sectionData.actions.map((a) => ({
            ...a,
            value: false,
          }));
        }
      }

      if (section === "fleets" && actionName === PermissionActions.VIEW) {
        const fleetView = sectionData.actions.find(
          (a) => a.name === PermissionActions.VIEW
        )?.value;

        if (!fleetView) {
          ["taskSchedular", "otaUpdate"].forEach((key) => {
            if (updated[key]) {
              updated[key].actions = updated[key].actions.map((a) =>
                a.name === PermissionActions.SCHEDULE
                  ? { ...a, value: false }
                  : a
              );
            }
          });
        }
      }

      return updated;
    });
  };

  const updatePermissions = async () => {
    try {
      await axiosLLInstance.put(`/users/${userId}/permissions`, permissions);
      notifyUser({
        message: "Permissions updated",
        type: "success",
      });
    } catch (error) {
      console.error("Error updating permissions", error);
      notifyUser({
        message: "Failed to update permissions",
        type: "error",
      });
    }
  };

  return (
    <div className="flex justify-center mt-8">
      <div className="w-[80%]">
        <div className="ms-4">
          <BreadcrumbItems
            items={[
              { title: "Manage Users", to: "/users" },
              { title: "Assign Rights" },
            ]}
          />
        </div>

        <div className="flex items-center justify-between flex-col my-10">
          <div className="text-[29px] font-[500] leading-[29px] text-center text-gray-900">
            Change Rights for {user?.name || "User"}
          </div>
        </div>

        <div className="mt-6">
          <div className="overflow-x-auto">
            <p className="text-gray-700 text-[17px] font-[500] mb-4">
              User Permissions
            </p>

            <div className="flex flex-col gap-4">
              {Object.entries(permissions).map(([key, module]) => (
                <div
                  key={key}
                  className="bg-white border border-gray-200 rounded-xl shadow-sm p-5"
                >
                  <p className="text-gray-800 text-[16px] font-semibold mb-2">{module.title}</p>

                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {module.actions.map((action) => {
                      const isDisabled =
                        (action.name !== PermissionActions.VIEW &&
                          !module.actions.find(
                            (a) => a.name === PermissionActions.VIEW
                          )?.value) ||
                        (action.name === PermissionActions.SCHEDULE &&
                          permissions.fleets.actions.find(
                            (a) => a.name === PermissionActions.VIEW
                          )?.value === false);

                      return (
                        <div
                          key={action.name}
                          className="flex items-center justify-between bg-gray-50 border rounded-lg p-2"
                        >
                          <span className="capitalize text-gray-700">
                            {action.name}
                          </span>
                          <label className="inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={action.value}
                              onChange={() =>
                                togglePermission(key, action.name)
                              }
                              className="sr-only peer"
                              disabled={isDisabled}
                            />
                            <div
                              className={`w-5 h-5 rounded-full transition relative ${
                                isDisabled
                                  ? "bg-gray-200 cursor-not-allowed"
                                  : "bg-gray-300 peer-checked:bg-blue-500"
                              }`}
                            ></div>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end w-full flex-wrap mt-4">
          <button
            className="btn bg-slate-950 text-slate-50 text-[16px] font-[500] border rounded-xl w-40 hover:bg-slate-800 transition"
            onClick={updatePermissions}
          >
            Update
          </button>
        </div>
      </div>
    </div>
  );
}
